import { useState, useEffect } from "react"
import { PanelSkeleton } from "components/new-stuff/ui/Skeleton"
import { Link, useNavigate } from "react-router-dom"
import { SetupMessageAction } from "./SetupMessageAction"
import { EventTagsBirthday } from "./EventTagsBirthday"
import { EventTagsAnniversaries } from "./EventTagsAnniversaries"
import { EventTagsLeaves } from "./EventTagsLeaves"
import { TaskTagsRoutine } from "./TaskTagsRoutine"
import { IDashboard, IDashboardRes, IPromotionRes } from "../typings"
import { useQuery } from "@tanstack/react-query"
import {
  DASHBOARD_ENDPOINTS,
  dashboardService,
} from "services/dashboardService"
import { EWA_ENDPOINTS, ewaService } from "services/ewaService"
import CustomCard from "components/new-stuff/ui/CustomCard"
import { Carousel, Select } from "antd"
import { isPadeDomain } from "utils/utils"
import { filterOptions, getDateRange } from "utils/formatDate"

export const Actions = ({
  actionData,
  isActionDataLoading,
}: {
  actionData: IDashboard | undefined
  isActionDataLoading: boolean
}) => {
  const navigate = useNavigate()

  const [activeButtonTasks, setActiveButtonTasks] = useState("task-1")
  const [activeButtonEvents, setActiveButtonEvents] = useState("event-1")
  const [eventsFilter, setEventsFilter] = useState("this-month")
  const [setupMessages, setSetupMessages] = useState<any>([])

  const { startDate, endDate } = getDateRange(eventsFilter)

  const { isLoading: isEventsLoading, data: eventsApiData } =
    useQuery<IDashboardRes>({
      queryKey: [DASHBOARD_ENDPOINTS.DASHBOARD_EVENTS, startDate, endDate],
      queryFn: () =>
        dashboardService.getDashboardEvents({
          start_date: startDate,
          end_date: endDate,
        }),
    })

  const { data: leavesApiData } = useQuery<IDashboardRes>({
    queryKey: [DASHBOARD_ENDPOINTS.DASHBOARD_ACTIONS, startDate, endDate],
    queryFn: () =>
      dashboardService.getDashboardActions({
        start_date: startDate,
        end_date: endDate,
      }),
  })

  const filteredBirthdays = eventsApiData?.data?.birthdayPersons ?? []
  const filteredAnniversaries = eventsApiData?.data?.anniversaryPersons ?? []
  const filteredLeaves = leavesApiData?.data?.leaveRequests ?? []

  const { isLoading: isCampaignsLoading, data: campaignData } =
    useQuery<IPromotionRes>({
      queryKey: [EWA_ENDPOINTS.PROMOTIONS, "published"],
      queryFn: () => ewaService.getPromotions({ status: "published" }),
      enabled: isPadeDomain(),
    })

  useEffect(() => {
    let SetSubArray: any[] = []
    if (
      actionData?.person?.exitRequests &&
      actionData?.person?.exitRequests > 0
    ) {
      SetSubArray = [
        ...SetSubArray,
        {
          subtitle: `You have ${actionData?.person?.exitRequests} exit request${
            actionData?.person?.exitRequests > 1 ? "s" : ""
          } to review.`,
          url: "/approval/requests/exits",
        },
      ]
    }
    if (
      actionData?.person?.leaveRequests &&
      actionData?.person?.leaveRequests > 0
    ) {
      SetSubArray = [
        ...SetSubArray,
        {
          subtitle: `You have ${
            actionData?.person?.leaveRequests
          } leave request${
            actionData?.person?.leaveRequests > 1 ? "s" : ""
          } to review.`,
          url: "/approval/requests/leaves",
        },
      ]
    }
    if (
      actionData?.person?.loanRequests &&
      actionData?.person?.loanRequests > 0
    ) {
      SetSubArray = [
        ...SetSubArray,
        {
          subtitle: `You have ${actionData?.person?.loanRequests} loan request${
            actionData?.person?.loanRequests > 1 ? "s" : ""
          } to review.`,
          url: "/approval/requests/loans",
        },
      ]
    }
    if (actionData?.person?.payrolls && actionData?.person?.payrolls > 0) {
      SetSubArray = [
        ...SetSubArray,
        {
          subtitle: `You have ${actionData?.person?.payrolls} payroll${
            actionData?.person?.payrolls > 1 ? "s" : ""
          } to review.`,
          url: "/approval/payrolls/records",
        },
      ]
    }
    if (
      actionData?.person?.payrollChanges &&
      actionData?.person?.payrollChanges > 0
    ) {
      SetSubArray = [
        ...SetSubArray,
        {
          subtitle: `You have ${
            actionData?.person?.payrollChanges
          } payroll change${
            actionData?.person?.payrollChanges > 1 ? "s" : ""
          } to review.`,
          url: "/approval/payrolls/changes",
        },
      ]
    }

    if (
      actionData?.person?.redeploymentRequests &&
      actionData?.person?.redeploymentRequests > 0
    ) {
      SetSubArray = [
        ...SetSubArray,
        {
          subtitle: `You have ${
            actionData?.person?.redeploymentRequests
          } redeployment request${
            actionData?.person?.redeploymentRequests > 1 ? "s" : ""
          } to review.`,
          url: "/approval/requests/redeployments",
        },
      ]
    }
    if (
      actionData?.person?.salaryAdvanceRequests &&
      actionData?.person?.salaryAdvanceRequests > 0
    ) {
      SetSubArray = [
        ...SetSubArray,
        {
          subtitle: `You have ${
            actionData?.person?.salaryAdvanceRequests
          } salary advance request${
            actionData?.person?.salaryAdvanceRequests > 1 ? "s" : ""
          } to review.`,
          url: "/approval/requests/salary-advances",
        },
      ]
    }
    if (actionData?.person?.violations && actionData?.person?.violations > 0) {
      SetSubArray = [
        ...SetSubArray,
        {
          subtitle: `You have ${actionData?.person?.violations} violation${
            actionData?.person?.violations > 1 ? "s" : ""
          } to review.`,
          url: "/approval/violations",
        },
      ]
    }
    if (
      actionData?.person?.suspensions &&
      actionData?.person?.suspensions > 0
    ) {
      SetSubArray = [
        ...SetSubArray,
        {
          subtitle: `You have ${actionData?.person?.suspensions} suspension${
            actionData?.person?.suspensions > 1 ? "s" : ""
          } to review.`,
          url: "/approval/disciplinary/suspensions",
        },
      ]
    }
    if (
      actionData?.person?.customPaymentSchedules &&
      actionData?.person?.customPaymentSchedules > 0
    ) {
      SetSubArray = [
        ...SetSubArray,
        {
          subtitle: `You have ${
            actionData?.person?.customPaymentSchedules
          } custom payment schedule${
            actionData?.person?.customPaymentSchedules > 1 ? "s" : ""
          } to review.`,
          url: "/approval/payment-schedules/custom",
        },
      ]
    }
    if (
      actionData?.person?.confirmations &&
      actionData?.person?.confirmations > 0
    ) {
      SetSubArray = [
        ...SetSubArray,
        {
          // Due for confirmation, not "to review": nothing has been submitted
          // here. Probation has simply run out and the decision is the admin's
          // to make. The reminder email lands on this same page.
          subtitle: `You have ${actionData?.person?.confirmations} employee${
            actionData?.person?.confirmations > 1 ? "s" : ""
          } due for confirmation.`,
          url: "/dashboard/confirmation",
        },
      ]
    }
    if (
      actionData?.person?.employeeProfileUpdateRequests &&
      actionData?.person?.employeeProfileUpdateRequests > 0
    ) {
      SetSubArray = [
        ...SetSubArray,
        {
          subtitle: `You have ${
            actionData?.person?.employeeProfileUpdateRequests
          } employee profile request${
            actionData?.person?.employeeProfileUpdateRequests > 1 ? "s" : ""
          } to review.`,
          url: "/approval/employee-profile-updates",
        },
      ]
    }
    if (
      actionData?.person?.confirmations &&
      actionData?.person?.confirmations > 0
    ) {
      SetSubArray = [
        ...SetSubArray,
        {
          // Due for confirmation, not "to review": nothing has been submitted
          // here. Probation has simply run out and the decision is the admin's
          // to make. The reminder email lands on this same page.
          subtitle: `You have ${actionData?.person?.confirmations} employee${
            actionData?.person?.confirmations > 1 ? "s" : ""
          } due for confirmation.`,
          url: "/dashboard/confirmation",
        },
      ]
    }
    setSetupMessages([...setupMessages, ...SetSubArray])
  }, [actionData])

  return (
    <div className="">
      {/* <h5 className="text-base font-semibold text-padeBlack font-circular   mb-4">
        Actions
      </h5> */}

      <div className="grid  sm:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5  xl:h-[390px]">
        <CustomCard
          title="Tasks"
          activeTabKey={activeButtonTasks}
          onTabChange={(key) => setActiveButtonTasks(key as string)}
          rootClassName="h-auto xl:h-[390px]"
          tabList={[
            {
              key: "task-1",
              tab: (
                <div className="max-h-[260px] overflow-y-auto  ">
                  <SetupMessageAction setupArray={setupMessages} />
                </div>
              ),
              label: "Actions",
            },
            {
              key: "task-2",
              tab: (
                <div className="max-h-[260px] overflow-y-auto  ">
                  <TaskTagsRoutine
                    taskArray={actionData?.person?.employeeRoutineTasks}
                  />
                </div>
              ),
              label: "Routine",
            },
            {
              key: "task-3",
              tab: (
                <div className="max-h-[260px] overflow-y-auto  ">
                  <TaskTagsRoutine
                    taskArray={actionData?.person?.employeeOnboardingTasks}
                  />
                </div>
              ),
              label: "Onboarding",
            },
          ]}
        />

        <CustomCard
          title="Events"
          activeTabKey={activeButtonEvents}
          rootClassName="h-auto xl:h-[390px]"
          onTabChange={(key) => setActiveButtonEvents(key as string)}
          ctaBtn={
            <Select
              value={eventsFilter}
              onChange={(val) => setEventsFilter(val)}
              placeholder="Filter"
              size="small"
              className="min-w-[100px] text-sm"
              options={filterOptions}
            />
          }
          tabList={[
            {
              key: "event-1",
              tab: (
                <div className="h-[270px]  overflow-y-auto ">
                  <EventTagsBirthday eventArray={filteredBirthdays} />
                </div>
              ),
              label: "Birthdays",
            },
            {
              key: "event-2",
              tab: (
                <div className="h-[270px] overflow-y-auto ">
                  <EventTagsAnniversaries eventArray={filteredAnniversaries} />
                </div>
              ),
              label: "Anniversaries",
            },
            {
              key: "event-3",
              tab: (
                <div className="h-[270px]  overflow-y-auto ">
                  <EventTagsLeaves eventArray={filteredLeaves} />
                </div>
              ),
              label: "Leave",
            },
          ]}
          loading={isEventsLoading}
        />
        {isPadeDomain() === true && (
          <div className="w-full  h-auto xl:h-[390px] rounded-[5px] overflow-hidden">
            {isCampaignsLoading ? (
              <div
                className="h-full w-full p-5"
                aria-busy="true"
              >
                <PanelSkeleton size="page" />
              </div>
            ) : campaignData?.data?.docs?.length ? (
              <div className="h-full w-full">
                <Carousel
                  autoplay
                  effect="fade"
                  dots={{
                    className:
                      "!bottom-[25px] [&_button]:!rounded-full [&_button]:!w-[12px] [&_button]:!h-[12px] [&_button]:!bg-a11y",
                  }}
                  rootClassName=" h-full w-full "
                >
                  {campaignData?.data?.docs?.map((data: any, index: any) => {
                    if (
                      (data?.platform === "web" || data?.platform === "all") &&
                      data?.status === "published" &&
                      (data?.audience === "all" || data?.audience === "admin")
                    ) {
                      return (
                        <div
                          className="h-auto xl:h-[390px] w-full "
                          key={index}
                        >
                          <Link
                            className="h-full w-full"
                            to={data?.ctaLink ? data?.ctaLink : ""}
                            target="_blank"
                          >
                            <div
                              style={{
                                backgroundImage: `url(${data?.webImage})`,
                                backgroundSize: "110%",
                                backgroundRepeat: "no-repeat",
                                backgroundPosition: "center",

                                height: "100%",
                              }}
                              className="h-full w-full cursor-pointer  "
                            >
                              <img
                                src={data?.webImage}
                                alt=""
                                style={{
                                  visibility: "hidden",
                                  width: "100%",
                                  height: "100%",
                                }}
                              />
                            </div>
                          </Link>
                        </div>
                      )
                    } else {
                      return null
                    }
                  })}
                </Carousel>
              </div>
            ) : (
              <div>Your campaign will appear here</div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
